import MobileGridView from './MobileGridView';

import DesktopGridView from './DesktopGridView';

// eslint-disable-next-line no-restricted-syntax -- pre-existing raw Typography (a styled text button and hints the closed DS Typography can't express); migrate with the rest of grid-data.
import { TableContainer, Box, styled, Typography, useMediaQuery, useTheme } from '@mui/material';

import { alpha } from '@mui/material/styles';

import { Loader } from '@sm-dashboard/ui-components';

import { GridProps, ColumnType } from '@sm-dashboard/types';

import Filters from '../filters/Filters';

import { Pagination } from '../pagination/Pagination';

import { GridProvider } from './GridContext';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { sortDataByColumn, getSortValue } from '../helpFunctions';

import { MobileGridResultsCount } from './MobileGridResultsCount';

import { SelectedRowsCount } from './SelectedRowsCount';

import { DESKTOP_TABLE_MIN_HEIGHT, GRID_PAGE_GUTTER } from './gridPageGutter';

const GridData = <T, U = any>({ ...props }: GridProps<T, U>) => {

  const {

    data,

    loading,

    filters,

    promoteFirstFilterToPrimary,

    filtersDefaultExpanded,

    exportConfig,

    pagination,

    mobileInfinite,

    components,

    style,

    tableRef,

    selectable,

    selectActionsRender,

    mobileBulkSelectMode = false,

    onMobileBulkSelectModeChange,

    onSort: externalOnSort,

    sortField: externalSortField,

    sortDirection: externalSortDirection,

    sortable,

    columns

  } = props;



  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const assignScrollContainerRef = useCallback(

    (node: HTMLElement | null) => {

      scrollContainerRef.current = node;

      if (tableRef) {

        tableRef.current = node;

      }

    },

    [tableRef]

  );

  const resolvedTableRef = tableRef ?? scrollContainerRef;



  const mobileInfiniteScroll = Boolean(

    isSmallScreen && mobileInfinite && pagination && (pagination.count ?? 0) > 0

  );



  const [infiniteRows, setInfiniteRows] = useState<T[] | undefined>(undefined);

  const infinitePageRef = useRef(0);

  const infiniteRowsRef = useRef<T[] | undefined>(undefined);

  const infiniteLoadingRef = useRef(false);

  const [infiniteLoadingUi, setInfiniteLoadingUi] = useState(false);

  infiniteRowsRef.current = infiniteRows;



  useEffect(() => {

    if (!isSmallScreen) {

      setInfiniteRows(undefined);

    }

  }, [isSmallScreen]);



  useEffect(() => {

    if (!mobileInfiniteScroll || !mobileInfinite) {

      return;

    }

    infinitePageRef.current = mobileInfinite.lastLoadedPageIndex;

    setInfiniteRows([...data]);

  }, [mobileInfiniteScroll, mobileInfinite?.dataKey, mobileInfinite?.lastLoadedPageIndex, data, mobileInfinite]);



  const [internalSortField, setInternalSortField] = useState<string | null>(null);

  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc' | null>(null);

  const sortField = externalSortField ?? internalSortField;

  const sortDirection = externalSortDirection ?? internalSortDirection;

  const hasExternalSorting = !!externalOnSort;

  const handleInternalSort = useCallback((field: string, direction: 'asc' | 'desc' | null) => {

    if (direction) {

      setInternalSortField(field);

      setInternalSortDirection(direction);

    } else {

      setInternalSortField(null);

      setInternalSortDirection(null);

    }

  }, []);

  const handleSort = useCallback(

    (field: string, direction: 'asc' | 'desc' | null) => {

      if (externalOnSort) {

        externalOnSort(field, direction);

      } else {

        handleInternalSort(field, direction);

      }

    },

    [externalOnSort, handleInternalSort]

  );



  const viewSource: T[] = useMemo(

    () => (mobileInfiniteScroll && mobileInfinite ? infiniteRows ?? data : data),

    [mobileInfiniteScroll, mobileInfinite, infiniteRows, data]

  );



  const handleInfiniteLoad = useCallback(async () => {

    if (infiniteLoadingRef.current || !mobileInfinite) {

      return;

    }

    const total = pagination?.count ?? 0;

    if (total === 0) {

      return;

    }

    const currentBlock = infiniteRowsRef.current ?? data;

    if (currentBlock.length >= total) {

      return;

    }

    infiniteLoadingRef.current = true;

    setInfiniteLoadingUi(true);

    try {

      const next = infinitePageRef.current + 1;

      const newRows = await mobileInfinite.loadNextPage(next);

      infinitePageRef.current = next;

      setInfiniteRows(prev => [...(prev ?? data), ...newRows]);

    } finally {

      infiniteLoadingRef.current = false;

      setInfiniteLoadingUi(false);

    }

  }, [mobileInfinite, data, pagination?.count]);



  const sortedData = useMemo(() => {

    if (hasExternalSorting) {

      return viewSource;

    }



    if (!sortField || !sortDirection || !columns || !viewSource || viewSource.length === 0) {

      return viewSource;

    }

    const column = columns.find(col => {

      if (col.field !== sortField) return false;

      const allowedTypes: Array<ColumnType> = ['text', 'number', 'amount', 'date', 'dateTime'];

      if (col.type && !allowedTypes.includes(col.type)) {

        return false;

      }



      if (col.type === 'mailingApproval') return false;



      if (viewSource && viewSource.length > 0) {

        const sampleValue = getSortValue(viewSource[0], col);

        if (typeof sampleValue === 'boolean') {

          return false;

        }

      }



      if (sortable === false) return false;

      if (sortable === true) return col.sortable !== false;

      return col.sortable === true;

    });

    if (!column) {

      return viewSource;

    }

    return sortDataByColumn(viewSource, column, sortDirection);

  }, [viewSource, sortField, sortDirection, hasExternalSorting, columns, sortable]);



  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [selectedAll, setSelectedAll] = useState<boolean>(false);



  const resetSelections = useCallback(() => {

    setSelectedRows([]);

    setSelectedAll(false);

  }, []);



  const handleRowSelectionToggle = useCallback((index: number) => {

    setSelectedRows(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]));

    setSelectedAll(false);

  }, []);



  const handleRowSelectionSelect = useCallback((index: number) => {

    setSelectedRows(prev => (prev.includes(index) ? prev : [...prev, index]));

    setSelectedAll(false);

  }, []);



  const exitMobileBulkSelectMode = useCallback(() => {

    onMobileBulkSelectModeChange?.(false);

    resetSelections();

  }, [onMobileBulkSelectModeChange, resetSelections]);



  useEffect(() => {

    if (!mobileBulkSelectMode) {

      resetSelections();

    }

  }, [mobileBulkSelectMode, resetSelections]);



  useEffect(() => {

    if (!isSmallScreen && mobileBulkSelectMode) {

      onMobileBulkSelectModeChange?.(false);

    }

  }, [isSmallScreen, mobileBulkSelectMode, onMobileBulkSelectModeChange]);



  useEffect(() => {
    if (!mobileInfiniteScroll || !data) {
      return;
    }
    const el = resolvedTableRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => {
      const total = pagination?.count ?? 0;
      const currentLen = (infiniteRowsRef.current ?? data)?.length ?? 0;
      if (total > 0 && currentLen >= total) {
        return;
      }
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 480) {
        void handleInfiniteLoad();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [mobileInfiniteScroll, data, handleInfiniteLoad, pagination?.count, infiniteRows]);

  if (!data) {

    return <></>;

  }



  const displayData: T[] = hasExternalSorting ? viewSource : sortedData;

  const mobileHasMore = displayData.length < (pagination?.count ?? 0);

  const mobileVirtualInfinite = mobileInfiniteScroll
    ? {
        hasMore: mobileHasMore,
        isLoadingMore: infiniteLoadingUi,
        onLoadMore: () => {
          void handleInfiniteLoad();
        }
      }
    : undefined;

  const mobileSortProps = {

    onSort: handleSort,

    sortField,

    sortDirection,

    sortable

  };



  const mobileView = (

    <MobileGridView

      {...props}

      tableRef={resolvedTableRef}

      data={displayData}

      // Keep false: tanstack virtual list + absolute rows does not scroll in native iOS WebView.
      // Infinite pages still append via scroll listener below / onLoadMore path.
      contentFillsScrollParent={false}

      mobileVirtualInfinite={mobileVirtualInfinite}

      mobileBulkSelectMode={mobileBulkSelectMode}

      selectedRows={selectedRows}

      onRowSelectionToggle={handleRowSelectionToggle}

      onRowSelectionSelect={handleRowSelectionSelect}

      {...mobileSortProps}

    />

  );



  const pageGutter = style?.padding ?? GRID_PAGE_GUTTER;
  const paddedSectionSx =
    pageGutter !== 0 ? { px: pageGutter, width: '100%', boxSizing: 'border-box' as const } : { width: '100%' };

  return (

    <Box

      sx={{

        px: 0,

        pt: style?.paddingTop ?? style?.padding ?? 2,

        pb: 0,

        display: 'flex',

        flexDirection: 'column',

        justifyContent: 'flex-start',

        alignItems: 'stretch',

        width: '100%',

        maxWidth: '100%',

        position: 'relative',

        height: '100%',

        minHeight: isSmallScreen ? '70vh' : 0,

        overflow: 'hidden',

        boxSizing: 'border-box'

      }}

    >

      {/* A fixed 16px under the filters, whether their panel is open or closed. */}

      <Box sx={{ flexShrink: 0, ...paddedSectionSx, pb: 2 }}>

        <Filters

          filters={filters}

          promoteFirstRegularToPrimary={promoteFirstFilterToPrimary}

          defaultExpanded={filtersDefaultExpanded}

          exportConfig={exportConfig}

          filtersAttachedButton={components?.filtersAttachedButton}

          flushBottom

        />

      </Box>



      {components?.AfterFilters && <Box sx={{ flexShrink: 0, ...paddedSectionSx }}>{components.AfterFilters}</Box>}

      {components?.Toolbar && (pagination?.count ?? 0) > 0 && (

        <Box sx={{ flexShrink: 0, ...paddedSectionSx }}>{components.Toolbar}</Box>

      )}

      {isSmallScreen && selectable && mobileBulkSelectMode && (

        <Box sx={{ flexShrink: 0, ...paddedSectionSx, pb: 1 }}>

          <Box

            sx={{

              display: 'flex',

              flexDirection: 'column',

              gap: 1,

              p: 1.5,

              borderRadius: 0.5,

              bgcolor: 'background.paper',

              border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`

            }}

          >

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>

              {selectedRows.length > 0 ? (

                <SelectedRowsCount count={selectedRows.length} />

              ) : (

                <Typography variant="body2" color="text.secondary">

                  הקש על שורות לבחירה

                </Typography>

              )}

              <Typography

                component="button"

                type="button"

                variant="body2"

                onClick={exitMobileBulkSelectMode}

                sx={{

                  border: 'none',

                  background: 'none',

                  cursor: 'pointer',

                  color: 'primary.main',

                  fontWeight: 600,

                  p: 0,

                  flexShrink: 0

                }}

              >

                סיום

              </Typography>

            </Box>

            {selectedRows.length > 0 && selectActionsRender ? (

              <Box sx={{ width: '100%' }}>

                {selectActionsRender(selectedRows, selectedAll, resetSelections, displayData)}

              </Box>

            ) : null}

          </Box>

        </Box>

      )}



      <TableContainer

        ref={assignScrollContainerRef}

        component={Box}

        sx={{

          bgcolor: isSmallScreen ? 'background.paper' : 'background.default',

          flexGrow: 1,

          flexShrink: 1,

          // Mobile infinite/virtual lists need flexBasis 0 so this scrolls instead of growing.

          flexBasis: isSmallScreen ? 0 : 'auto',

          width: '100%',

          overflow: 'auto',

          overflowAnchor: 'none',

          WebkitOverflowScrolling: 'touch',

          overscrollBehavior: 'contain',

          height: 'auto',

          minHeight: isSmallScreen ? 0 : DESKTOP_TABLE_MIN_HEIGHT,

          maxHeight: '100%',

          pt: 0,

          border: theme =>

            `1px solid ${alpha(theme.palette.info.dark, 38 / 255)}`,

          boxSizing: 'border-box'
        }}

      >

        {loading ? (

          <LoadingState components={components} />

        ) : pagination?.count === 0 ? (

          <EmptyState components={components} />

        ) : isSmallScreen ? (

          mobileView

        ) : (

          <DesktopGridView

            {...props}

            data={displayData}

            selectedRows={selectedRows}

            setSelectedRows={setSelectedRows}

            selectedAll={selectedAll}

            setSelectedAll={setSelectedAll}

            resetSelections={resetSelections}

            {...mobileSortProps}

          />

        )}

      </TableContainer>



      {!isSmallScreen && (

        <Box sx={{ width: '100%', flexShrink: 0 }}>
          {pagination && <Pagination {...pagination} />}
        </Box>

      )}



      {isSmallScreen && mobileInfinite && !loading && (pagination?.count ?? 0) > 0 && (

        <MobileGridResultsCount count={pagination?.count ?? 0} />

      )}



      {components?.Footer && (

        <Box sx={{ width: '100%', flexShrink: 0 }}>

          <components.Footer data={displayData} />

        </Box>

      )}

    </Box>

  );

};



const LoadingState = ({ components }: Pick<GridProps<any, any>, 'components'>) => (

  <CenteredBox>{components?.LoadingOverlay ? <components.LoadingOverlay /> : <Loader />}</CenteredBox>

);



const EmptyState = ({ components }: Pick<GridProps<any, any>, 'components'>) => (

  <CenteredBox>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>

      {components?.NoRowsOverlay ? <components.NoRowsOverlay /> : <Typography>לא נמצאו שורות להצגה</Typography>}

      {components?.AddButton && components.AddButton}

    </Box>

  </CenteredBox>

);



const GridWrapper = <T, U = any>({ ...props }: GridProps<T, U>) => (

  <GridProvider>

    <GridData {...props} />

  </GridProvider>

);



const CenteredBox = styled(Box)({

  display: 'flex',

  justifyContent: 'center',

  alignItems: 'center',

  height: '100%',

  width: '100%'

});



export default GridWrapper;

